import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Newspaper } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';
import { stripHtml } from '../utils/newsContent';

const API = `${resolveBackendBaseUrl()}/api`;

const NewsSection = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`, { params: { limit: 3 } });
      const data = Array.isArray(response.data) ? response.data : [];
      setNews(data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const truncateContent = (content, length = 100) => {
    const plain = stripHtml(content || '');
    if (plain.length <= length) return plain;
    return plain.substring(0, length) + '...';
  };

  return (
    <section 
      id="berita" 
      className="py-24 md:py-32 bg-[#0A0A0A]"
      data-testid="news-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-widest uppercase text-brand-blue mb-4 block">
            Berita & Informasi
          </span>
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Update <span className="gradient-text-red">Terbaru</span> dari KTC
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Dapatkan informasi terkini tentang layanan, teknologi, dan promo spesial dari kami.
          </p>
        </motion.div>

        {/* News Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-4 border-brand-blue border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : news.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center py-16 bg-[#121212] rounded-2xl border border-white/10"
          >
            <Newspaper className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Belum ada berita tersedia</p>
            <p className="text-gray-500 text-sm mt-2">Pantau terus untuk update terbaru!</p>
          </motion.div>
        ) : (
          <>
            {/* News Grid */}
            <div className="grid md:grid-cols-3 gap-8">
              {news.map((item, index) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="service-card bg-[#121212] rounded-xl overflow-hidden group"
                  data-testid={`news-preview-${item.id}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={item.image_url || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600'}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-brand-red/90 text-white text-xs font-medium rounded-full">
                      {item.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(item.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {item.author}
                      </span>
                    </div>
                    <h3 className="font-outfit text-xl font-semibold text-white mb-3 line-clamp-2 group-hover:text-brand-blue transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {truncateContent(item.content)}
                    </p>
                    <Link 
                      to={`/berita/${item.id}`}
                      className="inline-flex items-center gap-2 text-brand-blue text-sm font-medium hover:text-brand-blue-neon transition-colors"
                    >
                      Baca Selengkapnya
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>

            {/* View All Button */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center mt-12"
            >
              <Link to="/berita">
                <Button 
                  variant="outline" 
                  className="btn-secondary px-8 py-3 rounded-full font-medium"
                  data-testid="view-all-news-button"
                >
                  Lihat Semua Berita
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
};

export default NewsSection;



